/// <reference path="../claims/stepperunemployment.js" />
import React, { useEffect, useState } from "react";
import {
    Grid,
    Button,
    Typography,
    TextField,
    IconButton,
    Tooltip
} from "@material-ui/core";
import { CloudDownload } from "@material-ui/icons";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import { useForm, useFormState } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import useComposeApplication from "../../application/hooks/useComposeApplication";
import { useAppState } from "../../application/AppState";
import useTranslation from "../../application/translation";
import { getDefaultValue2 } from "../../application/helpers/form";
import { formTypeEnum } from "../../application/enum";
import { InputFiles } from "../../application/helpers/InputFiles";
import Loading from "../../components/Loading";


export default function FileXForm({ selectedEventType, claimevent_Id }) {
    const [{ formValues, customer }] = useAppState();
    const [resultMessage, setResultMessage] = useState("");
    const [busy, setBusy] = React.useState(false);
    const application = useComposeApplication();
    const translation = useTranslation();

    const formType = formTypeEnum.unemploymentForm;

    const requiredObj = { required_error: translation.error_mandatory };
    const formSchema = z
        .object({
            email: z.string(requiredObj).email({ message: translation.error_invalidemail }),
        })
        .passthrough();

    const { register, handleSubmit, control, reset } = useForm({
        mode: "onChange",
        defaultValues: {
            email: customer.email,
            employerCertFile: getDefaultValue2(formValues, formType, "employerCertFile"),
            benefitsCertFile: getDefaultValue2(formValues, formType, "benefitsCertFile"),
            terminationCertFile: getDefaultValue2(formValues, formType, "terminationCertFile"),
            unemploymentCertFile:
                getDefaultValue2(formValues, formType, "unemploymentCertFile"),
            hospitalisationCertFile:
                getDefaultValue2(formValues, formType, "hospitalisationCertFile"),
        },
        resolver: zodResolver(formSchema),
    });

    const { errors, isValid } = useFormState({ control });

    useEffect(() => {
        reset();
    }, [reset]);

    const emptyApplication = async (data) => {        
        const allFilesEmpty = Object.values(data)
            .filter(value => value instanceof FileList) // only present FileLists
            .every(fileList => fileList.length === 0);       

        return allFilesEmpty;
    }

    const onSubmit = async (data) => {       
        const formData = { ...data, claimEventId: claimevent_Id };
        let isEmpty = await emptyApplication(formData);
        if (!isEmpty) {
            setBusy(true);
            const result = await application.update(selectedEventType, formData);
            reset();
            setResultMessage(result ? translation.update_thanks : translation.update_error);
            setBusy(false);
        } else {
            alert("Inga filer valda")
        }
    };

    if (busy) return <Loading label={""} />;

    return (
        <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
                {/* Info Tooltip */}
                <Grid item xs={12}>
                    <Typography variant="subtitle1">
                        {translation.file_attatchmentsInfo}
                        <Tooltip
                            id="fileAttachmentsDescription"
                            aria-label="filbilaga-information"
                            title={
                                <div>
                                    <strong>{translation.file_tooltip1}</strong><br /><br />
                                    {translation.file_tooltip2}<br /><br />
                                    {translation.file_tooltip3}
                                </div>
                            }
                        >
                            <IconButton aria-label="filbilaga-information">
                                <HelpOutlineIcon />
                            </IconButton>
                        </Tooltip>
                        :<br />
                    </Typography>
                    <Typography variant="body2">{translation.file_attachmentsInfo2}</Typography>
                </Grid>

                {/* Attachments */}
                {selectedEventType === 1 && (
                    <Grid item xs={12}>
                        <Typography variant="body2">{translation.workincap_employerCert}</Typography>
                        <InputFiles
                            fieldName="employerCertFile"
                            errors={errors}
                            register={register}
                            formType={formType}
                        />
                    </Grid>
                )}

                {selectedEventType === 2 && (
                    <>
                        <Grid item xs={12}>
                            <Typography variant="body2">{translation.unemp_file_empcert}</Typography>
                            <InputFiles
                                fieldName="employerCertFile"
                                errors={errors}
                                register={register}
                                formType={formType}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="body2">{translation.unemp_file_unempcert}</Typography>
                            <InputFiles
                                fieldName="unemploymentCertFile"
                                errors={errors}
                                register={register}
                                formType={formType}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="body2">{translation.unemp_file_terminationcert}</Typography>
                            <InputFiles
                                fieldName="terminationCertFile"
                                errors={errors}
                                register={register}
                                formType={formType}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="body2">{translation.unemp_file_payments}</Typography>
                            <InputFiles
                                fieldName="benefitsCertFile"
                                errors={errors}
                                register={register}
                                formType={formType}
                            />
                        </Grid>
                    </>
                )}

                {selectedEventType === 5 && (
                    <Grid item xs={12}>
                        <Typography variant="body2">{translation.hosp_file_cert}</Typography>
                        <InputFiles
                            fieldName="hospitalisationCertFile"
                            errors={errors}
                            register={register}
                            formType={formType}
                        />
                    </Grid>
                )}

                {/* Email & Submit */}
                <Grid item xs={12}>
                    <TextField
                        id="email"
                        {...register("email")}
                        label="E-post"
                        variant="outlined"
                        fullWidth
                        inputProps={{ "aria-label": "E-postadress" }}
                        error={!!errors.email}
                        helperText={errors.email?.message}
                    />
                </Grid>

                <Grid item xs={6}>
                    <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={!isValid}
                        aria-label={translation.button_send}
                    >
                        {translation.button_send}
                    </Button>
                </Grid>

                {(selectedEventType === 1 || selectedEventType === 5) && (
                    <Grid item xs={12}>
                        <Typography variant="body2">
                            {translation.sick_doctorsatt_info}
                        </Typography>
                        <Button
                            variant="text"
                            startIcon={<CloudDownload color="primary" />}
                            target="_blank"
                            href={`${process.env.PUBLIC_URL}/Documents/${translation.doctorAttFile}`}
                            aria-label={translation.sick_doctorslink}
                        >
                            {translation.sick_doctorslink}
                        </Button>
                    </Grid>
                )}
            </Grid>

            <Typography
                variant="body2"
                role="alert"
                aria-live="polite"
                style={{ marginTop: "1rem" }}
            >
                {resultMessage}
            </Typography>
        </form>
    );
}
